import React from "react";
import { motion } from "framer-motion";
import { FileText, Mail, FileSpreadsheet, TrendingUp, Handshake, Settings2, Lock, Plug, Zap, Crown } from "lucide-react";
import Reveal from "../components/Reveal";
import SectionDivider from "../components/SectionDivider";
import SEO from "../components/SEO";

const services = [
  { icon: FileText, title: "Document Triage System", popular: true, body: "Automatically processes every document that arrives in your inbox. Extracts key data, logs it to your dashboard, and notifies your team instantly. Works with invoices, contracts, proposals, rate confirmations, briefs, and more." },
  { icon: Mail, title: "Inquiry Response System", body: "Every inquiry email your business receives gets read, classified by intent, and responded to automatically. Serious prospects receive a professional response within minutes — 24 hours a day." },
  { icon: FileSpreadsheet, title: "Automated Quote System", body: "When a potential client reaches out, our system reads their message, understands what they need, and sends them a professional custom quote automatically. No manual back and forth." },
  { icon: TrendingUp, title: "Lead Follow Up System", body: "Never let a warm lead go cold. Our follow up system automatically sends timed follow up messages to prospects who haven't responded — stopping only when they reply or convert." },
  { icon: Handshake, title: "Client Onboarding System", body: "When a new client signs and pays, a fully automated onboarding sequence fires — welcome email, access requests, status page, and internal notifications — all without you lifting a finger." },
  { icon: Settings2, title: "Custom Build", body: "Have a specific workflow in mind? We'll build it. Tell us what's eating your team's time and we'll design a custom automation solution around your exact process." },
];

const reasons = [
  { icon: Lock, title: "No Lock-In Contracts", body: "Cancel your monthly plan anytime with written notice. No penalties." },
  { icon: Plug, title: "Works With Your Tools", body: "We connect to the systems you already use. No software to learn." },
  { icon: Zap, title: "48-Hour Delivery", body: "Most builds are live within 48 hours of receiving your credentials." },
  { icon: Crown, title: "You Own Everything", body: "Your data, your system, your automation. We build it, you keep it." },
];

const Services = () => {
  return (
    <div data-testid="services-page">
      <SEO
        title="Automation Services — Zelaflo Labs"
        description="From document triage to client onboarding — Zelaflo Labs builds custom automation systems for small and mid-size businesses."
        path="/services"
      />
      <section className="relative pt-40 pb-20 overflow-hidden" data-testid="services-hero">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 z-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-[#7B2FBE] font-semibold mb-3">Services</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tighter max-w-4xl text-balance" style={{ color: "var(--text)" }}>
              What We Build <span className="text-[#7B2FBE]">For You.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-lg md:text-xl max-w-2xl" style={{ color: "var(--text-muted)" }}>
              Every system is custom built for your business. We don't sell software — we deliver working automation. Document automation, workflow automation, and invoice processing automation — all built for your exact process.
            </p>
          </Reveal>
        </div>
      </section>

      <SectionDivider />

      <section className="relative py-16 md:py-24" data-testid="services-grid-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
            {services.map((s, i) => (
              <Reveal key={i} delay={(i % 3) * 0.08}>
                <div
                  className="service-glow group relative p-7 md:p-8 rounded-2xl border h-full min-h-[320px] flex flex-col transition-all duration-500 hover:-translate-y-1"
                  style={{
                    backgroundColor: "var(--surface)",
                    borderColor: s.popular ? "rgba(123,47,190,0.5)" : "var(--border)",
                  }}
                  data-testid={`service-card-${i}`}
                >
                  {s.popular && (
                    <span className="zl-pill absolute top-4 right-4 z-10 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full" data-testid="most-popular-badge">
                      Most Popular
                    </span>
                  )}
                  <div className="w-14 h-14 shrink-0 rounded-xl bg-[#7B2FBE]/15 border border-[#7B2FBE]/30 flex items-center justify-center text-[#8A4FFF] mb-5 group-hover:rotate-[8deg] group-hover:scale-110 transition-all duration-500">
                    <s.icon size={24} />
                  </div>
                  <h3 className="font-heading text-xl font-semibold mb-3 tracking-tight" style={{ color: "var(--text)" }}>{s.title}</h3>
                  <p className="leading-relaxed text-[15px] flex-1" style={{ color: "var(--text-muted)" }}>{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <SectionDivider />

      <section className="relative py-24 md:py-32" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-[#7B2FBE] font-semibold mb-3">Why Zelaflo Labs</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter max-w-3xl" style={{ color: "var(--text)" }}>
              Built different from the start.
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
            {reasons.map((r, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div
                  className="service-glow group p-7 md:p-9 rounded-2xl border h-full transition-all duration-500 hover:-translate-y-1"
                  style={{ backgroundColor: "var(--surface)", borderColor: "var(--border)" }}
                >
                  <div className="flex items-start gap-5">
                    <div className="shrink-0 w-14 h-14 rounded-xl bg-[#7B2FBE] flex items-center justify-center text-white group-hover:scale-110 group-hover:rotate-[6deg] transition-transform duration-500 zl-on-purple">
                      <r.icon size={24} />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl md:text-2xl font-semibold tracking-tight mb-2" style={{ color: "var(--text)" }}>{r.title}</h3>
                      <p className="leading-relaxed" style={{ color: "var(--text-muted)" }}>{r.body}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
